import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import {
  AMENITY_NAMES,
  getAmenityNumberValue,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.utils";
import { IBroker } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  IPropertyDetails,
  parseIncomeRatioRanges,
} from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import { getScoreAvailableDate } from "./components/DragItem/dragItem.utils";

/**
 * The property side of a RentMatch score, in one place.
 *
 * Three screens compute this score: the candidate list, a row in it and the
 * detail panel behind that row. They each built this object themselves, which
 * is how a field can reach one of them and not the others. The student
 * residence rule is exactly that: it reads the property type, none of the three
 * passed it, and tests on the scoring function itself cannot see that, because
 * they hand it an object built by hand in the test.
 *
 * The candidate side is not here: each screen already holds its own contacts
 * and residents in a different shape.
 */
export const buildScorePropertyDetails = (
  property: IProperty | undefined,
  propertyAmenities: IPropertyAmenity[] | undefined,
  broker?: IBroker,
): IPropertyDetails => ({
  // 0 counts as no price, which is what the callers check before scoring.
  rent: (property?.price || 0) + ((property as any)?.costs || 0),
  // Decides the household rule for a student residence, where an empty bedroom
  // count means one room rather than an unknown one.
  typeId: property?.typeId,
  // 0 is a studio, not an unknown count: the scoring gives an unknown count
  // full marks and a studio full marks for one person only (DEV-3251).
  bedrooms: propertyAmenities
    ? getAmenityNumberValue(
        propertyAmenities,
        "rentio.default.room_count_bedroom",
      ) ?? null
    : null,
  availableDate: getScoreAvailableDate(property),
  // What the broker set, from the two places it lives: this property's own
  // amenity rows and the office table on the broker record. Absent on almost
  // every property, and then the scoring decides exactly as it always did.
  settings: {
    perfectIncome: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.minimumIncome,
    ),
    maxHousehold: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.maxOccupants,
    ),
    incomeRatioRanges: parseIncomeRatioRanges(broker?.rentmatchIncomeRanges),
  },
  // Not scored today. They are in the scoring function's shape, so they are
  // stated rather than left out.
  hasBalcony: false,
  hasGarden: false,
  floorType: "other",
});
